import { createContext, useContext } from 'react';
import { ControlState } from './ControlTypes';

/**
 * Per-row UI state for all loaded structures.
 *
 * `controlsMap` is keyed by `row_id` (the canonical row identifier) and holds the
 * current `ControlState` for each result row. It lives in React state so that any
 * change triggers a re-render of components that depend on a particular row's state.
 *
 * `editingRowId` and `infoRowId` are mutually exclusive: opening one panel closes the
 * other (enforced inside `AhojTableBase.toggleEditPanel` / `toggleInfoPanel`).
 *
 * `colorPairCounts` tracks how many rows are currently using each color-pair index so
 * that `selectNextColorPair` can assign the least-used pair to the next activated row.
 */
export interface StructureStateContextType {
   /** Per-row viewer state, keyed by `row_id`. */
   controlsMap: Map<string, ControlState>;
   /** Immutably update or insert a `ControlState` for the given row. */
   setControl: (rowId: string, control: ControlState) => void;
   /** `row_id` of the row whose edit panel is currently open, or `null`. */
   editingRowId: string | null;
   setEditingRowId: (id: string | null) => void;
   /** `row_id` of the row whose info panel is currently open, or `null`. */
   infoRowId: string | null;
   setInfoRowId: (id: string | null) => void;
   /** Usage counts per color-pair index (from `COLOR_PAIRS`), used for least-used assignment. */
   colorPairCounts: Map<number, number>;
}

export const StructureStateContext = createContext<StructureStateContextType>({} as StructureStateContextType);

/** Convenience hook for consuming `StructureStateContext` in function components. */
export const useStructureState = (): StructureStateContextType => useContext(StructureStateContext);
